import { AfterViewInit, Component, computed, effect, EventEmitter, inject, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ActivatedRoute } from '@angular/router';
import { ManageTeamMembersComponent } from '@common/components/manage-team-members/manage-team-members';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { LookupListItem } from '@common/data-models/complex-types/lookup-list';
import { DataStore } from '@common/data-models/data-store/data-store';
import { SearchViewStateRecord } from '@common/data-models/records/search-viewstate-record';
import { UsersResults } from '@common/data-models/search-results/users-result';
import { Staff } from '@common/data-models/service/staff';
import { TeamMembersService } from '@common/services/team-members-service';
import { NavigationActions } from '@common/store/navigation/actions/navigation.actions';
import { NavigationItemTypes } from '@common/store/navigation/models/navigation.models';
import { selectCurrentItem } from '@common/store/navigation/selectors/navigation.selectors';
import { Store } from '@ngrx/store';
import { CastListComponent } from '@src/suite/cast/components/cast-list/cast-list';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { from, Observable, of, Subject, timer } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'st-search-box',
    templateUrl: './search-box.html',
    styleUrls: ['./search-box.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-search-box',
    },
})
export class SearchBoxComponent extends EnpsControlComponent<string> implements OnInit, AfterViewInit, OnDestroy {
    teamMembersService = inject(TeamMembersService);
    store = inject(Store);
    activatedRoute = inject(ActivatedRoute);
    searchFilter = new FormControl<string | LookupListItem | Staff>('');

    executeSearchSubj = new Subject<string>();
    @Output() public executeCastSearch = new EventEmitter();

    @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

    suggestions: Observable<(LookupListItem | Staff | string)[]>;
    public isStaffSearch: boolean = false;

    get allUsers(): Staff[] {
        return Object.values(this.globalTablesService.Staff);
    }

    searchTherm: string;

    searchOpen: boolean = false;

    item = computed(() => {
        return this.store.selectSignal(selectCurrentItem)();
    });

    constructor() {
        super();
        effect(() => {
            const item = this.item();
            if (item && item?.viewState?.viewStateType === ViewStateTypesEnum.SEARCH) {
                this.searchFilter.patchValue((<SearchViewStateRecord>item.viewState).viewStateSearchQuery);
            } else {
                this.searchFilter.setValue('');
            }
        });
    }

    destroy: Subject<void> = new Subject<void>();

    ngOnInit() {
        this.suggestions = this.searchFilter.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            switchMap(value => this.getSuggestions(value)),
            takeUntil(this.destroy)
        );
        this.executeSearchSubj
            .pipe(
                debounceTime(300),
                switchMap(value => of(value)),
                takeUntil(this.destroy)
            )
            .subscribe(value => {
                this.executeSearch(value);
            });
    }

    ngAfterViewInit() {
        this.activatedRoute.firstChild.paramMap.pipe(takeUntil(this.destroy)).subscribe(params => {
            if (params.get('submenu') === 'search') {
                this.openSearch();
            } else {
                this.searchTherm = '';
                this.closeSearch();
            }
            if (this.item() && this.item().guid === NavigationItemTypes.SYSTEM_SEARCH_GUID && this.item().viewState && this.item().viewState instanceof SearchViewStateRecord) {
                let query = (<SearchViewStateRecord>this.item().viewState).viewStateSearchQuery;

                if (query) {
                    query = decodeURIComponent(query);
                    if (query.startsWith('#')) {
                        this.isStaffSearch = false;
                        const tag = query.replace('#', '').toLowerCase();
                        if (tag) {
                            this.searchFilter.patchValue(new LookupListItem(tag, tag, tag));
                        }
                    } else if (query.startsWith('@')) {
                        this.isStaffSearch = true;
                        const staff = this.allUsers.find(option => option.UserID.toLowerCase() === query.replace('@', '').toLowerCase());
                        if (staff) {
                            this.searchFilter.patchValue(staff);
                        }
                    } else {
                        this.isStaffSearch = false;
                        this.searchFilter.patchValue(query);
                    }
                } else {
                    this.searchFilter.patchValue('');
                }
            }
        });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.destroy.next();
        this.destroy.complete();
    }

    public getSuggestions(val: string | LookupListItem | Staff): Observable<LookupListItem[] | Staff[]> {
        if (!val || val instanceof LookupListItem || val instanceof Staff) {
            this.isStaffSearch = false;
            return of([]);
        }

        val = val?.trim() as string;
        if (val?.length > 1) {
            if (val.startsWith('#') && val.length > 1) {
                val = val.replace('#', '').toLowerCase();
                this.isStaffSearch = false;
                return from(this.appService.nomWebServicesJson.getSuggestions(val)).pipe(map(res => res.data.map(item => new LookupListItem(item, item, item))));
            }
            if (val.startsWith('@') && val.length > 2) {
                val = val.replace('@', '').toLowerCase();

                const data = this.allUsers
                    .filter(option => option.UserID.toLowerCase().includes(val as string) || `${option.GivenName} ${option.Surname}`.toLowerCase().includes(val as string))
                    .map(item => item);
                this.isStaffSearch = true;
                return of(data);
            }
        }
        this.isStaffSearch = false;
        return of([]);
    }

    displayFn(item: LookupListItem | Staff | string): string {
        if (item instanceof LookupListItem) {
            return `#${item.id}`;
        }
        if (item instanceof Staff) {
            return `@${item.GivenName} ${item.Surname}`;
        }
        if (item) {
            return item;
        }
        return '';
    }

    getDisplayValue(item: LookupListItem | Staff | string): string {
        if (item instanceof LookupListItem) {
            return item.id;
        }
        if (item instanceof Staff) {
            return `${item.GivenName} ${item.Surname} ${item.Email ? item.Email : ''} ${item.Title ? item.Title : ''} ${item.Phone ? item.Phone : ''}`;
        }
        return '';
    }
    getUserId(item: LookupListItem | Staff | string): string {
        return item instanceof Staff ? item.UserID : null;
    }

    public onKeyDown(event: any) {
        // brackets ( ) shouldn't be allowed at all. we don't allow grouping at this level (and even if we do, it could mess solr query parsing and grouping)
        if (event.key === '(' || event.key === ')') {
            return false;
        }
    }

    public onKeyUp(event: any) {
        if (event.keyCode === 13 || event.key === 'Enter') {
            this.searchFn();
        }
    }

    parseSearchValue(value: string | LookupListItem | Staff): string {
        if (value instanceof LookupListItem) {
            return `#${value.id}`;
        }
        if (value instanceof Staff) {
            return `@${value.UserID}`;
        }
        if (value?.startsWith('@')) {
            return null;
        }
        return value?.trim();
    }

    optionSelected(event: MatAutocompleteSelectedEvent) {
        const value = this.parseSearchValue(event.option.value);
        if (!value) {
            return;
        }
        this.executeSearchSubj.next(value);
    }

    private async executeSearch(val: string) {
        if (!val?.startsWith('#') && !val?.startsWith('@') && this.appService.navigationService.currentListView instanceof ManageTeamMembersComponent) {
            const res = await this.teamMembersService.getTeamMembers(undefined, undefined, undefined, this.searchTherm);
            DataStore.usersResults = res.data.userResults;
            DataStore.usersResults.rebuildGroups(UsersResults.groupByField, UsersResults.sortByField);
        } else if (this.appService.navigationService.currentListView instanceof CastListComponent) {
            this.executeCastSearch.emit({ term: val });
        } else {
            this.store.dispatch(NavigationActions.enterSearchMode({ term: val }));
        }
    }

    public closeSearch() {
        this.autocomplete.closePanel();
        this.searchOpen = false;
    }

    public openSearch() {
        if (this.searchOpen) {
            this.searchFn();
        } else {
            this.searchOpen = true;
        }
        timer(0).subscribe(() => {
            this.autocomplete.closePanel();
            this.autocomplete.updatePosition();
        });
    }

    private async searchFn() {
        const { value } = this.searchFilter;
        const searchTerm: string = this.parseSearchValue(value);
        if (!searchTerm || searchTerm.trim().length === 0) {
            // Empty search
            if (this.appService.navigationService.currentListView instanceof ManageTeamMembersComponent) {
                const res = await this.teamMembersService.getTeamMembers();
                DataStore.usersResults = res.data.userResults;
                DataStore.usersResults.rebuildGroups(UsersResults.groupByField, UsersResults.sortByField);
            }
            return;
        }
        this.executeSearchSubj.next(searchTerm);
    }
}
